import { roleDisplayName } from '../../lib/roleDisplay';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useAuthStore } from '../../store/auth.store';
import { useEditionStore } from '../../store/edition.store';
import { adminVenueListOptions } from '../../hooks/adminVenueQueries';
import type { RoleDto } from '@pokerroom/types';
import {
  ROLE_ACTION_GROUPS,
  getRolePageDomains,
  actionPermissionLabelKey,
} from './rolePermissionOrder';

function MatrixContent({ revision }: { revision: number }) {
  const { t } = useTranslation(['admin', 'common']);
  const features = useEditionStore((s) => s.enabledFeatures);
  const systemUser = useAuthStore((s) => s.user?.isSystemUser);
  const {
    data: roles = [],
    isLoading,
    error,
    refetch,
  } = useQuery(adminVenueListOptions<RoleDto>('roles', revision));
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('all');
  const query = search.trim().toLocaleLowerCase();
  const matches = (...values: string[]) => values.join(' ').toLocaleLowerCase().includes(query);
  const actionGroups = ROLE_ACTION_GROUPS.filter(
    (g) => domain === 'all' || domain === g.labelKey,
  ).map((g) => ({
    ...g,
    subgroups: g.subgroups
      .map((sub) => ({
        ...sub,
        actions: sub.actions.filter((a) =>
          matches(a, t(actionPermissionLabelKey(a)), t(g.labelKey), t(sub.labelKey)),
        ),
      }))
      .filter((sub) => sub.actions.length),
  }));
  const pageGroups = getRolePageDomains(systemUser ? undefined : (key) => features.includes(key))
    .filter((g) => domain === 'all' || domain === g.labelKey)
    .map((g) => ({
      ...g,
      pages: g.pages.filter((p) =>
        matches(p.pagePath, t(p.labelKey, { ns: 'common' }), t(g.labelKey)),
      ),
    }));
  const cell = (allowed: boolean, bypass = false) => (
    <span
      aria-label={t(
        bypass ? 'permissionBypass' : allowed ? 'permissionAllowed' : 'permissionNotAllowed',
      )}
      className={allowed || bypass ? 'text-success' : 'text-base-content/50'}
    >
      {bypass ? t('permissionBypass') : allowed ? '✓' : '—'}
    </span>
  );
  const header = (
    <thead>
      <tr>
        <th className="border border-base-300 p-2 text-left min-w-[180px]">
          {t('permissionItem')}
        </th>
        {roles.map((r) => (
          <th key={r.id} className="border border-base-300 p-2 min-w-[100px]">
            {roleDisplayName(r, t)}
          </th>
        ))}
      </tr>
    </thead>
  );
  if (isLoading)
    return (
      <AdminPage header={<AdminPageHeader title={t('permissionMatrix')} />}>
        <p role="status">{t('loading')}</p>
      </AdminPage>
    );
  if (error)
    return (
      <AdminPage header={<AdminPageHeader title={t('permissionMatrix')} />}>
        <p role="alert">{t('permissionLoadFailed')}</p>
        <button type="button" onClick={() => refetch()}>
          {t('permissionRetryButton')}
        </button>
      </AdminPage>
    );
  return (
    <AdminPage
      className="admin-page-body"
      header={<AdminPageHeader title={t('permissionMatrix')} />}
    >
      <p className="text-sm mb-2">{t('permissionBoundary')}</p>
      <p className="text-sm mb-4">{t('permissionMatrixScope')}</p>
      {roles.some((r) => r.systemKey === 'SUPER_ADMIN') && (
        <p className="text-warning text-sm mb-3">{t('permissionSuperAdmin')}</p>
      )}
      <label className="block mb-3">
        {t('permissionSearch')}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="block w-full mt-1 p-2 rounded-lg bg-base-200 border border-base-300"
        />
      </label>
      <div className="flex flex-wrap gap-2 mb-5" aria-label={t('permissionDomains')}>
        {[{ labelKey: 'all' }, ...ROLE_ACTION_GROUPS].map((g) => (
          <button
            key={g.labelKey}
            type="button"
            aria-pressed={domain === g.labelKey}
            onClick={() => setDomain(g.labelKey)}
            className={`px-3 py-2 rounded-lg text-sm ${domain === g.labelKey ? 'bg-primary text-primary-content' : 'bg-base-200'}`}
          >
            {t(g.labelKey === 'all' ? 'permissionAllDomains' : g.labelKey)}
          </button>
        ))}
      </div>
      {!roles.length && <p>{t('noRoles')}</p>}
      <h2 className="text-lg font-medium mb-2">{t('pageVisibility')}</h2>
      <div className="overflow-x-auto mb-6">
        <table className="border-collapse w-full text-sm">
          {header}
          <tbody>
            {pageGroups.map(
              (g) =>
                g.pages.length > 0 && (
                  <React.Fragment key={g.labelKey}>
                    <tr>
                      <th colSpan={roles.length + 1} className="text-left p-2 bg-base-200">
                        {t(g.labelKey)}
                      </th>
                    </tr>
                    {g.pages.map((p) => (
                      <tr key={p.pagePath}>
                        <th className="border border-base-300 p-2 text-left font-normal">
                          {t(p.labelKey, { ns: 'common' })}
                          <small className="block text-base-content/60">{p.pagePath}</small>
                        </th>
                        {roles.map((r) => (
                          <td key={r.id} className="border border-base-300 p-2 text-center">
                            {cell(
                              r.pagePermissions.some(
                                (x) => x.pagePath === p.pagePath && x.isVisible,
                              ),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ),
            )}
          </tbody>
        </table>
      </div>
      <h2 className="text-lg font-medium mb-2">{t('actionPermissions')}</h2>
      <div className="overflow-x-auto">
        <table className="border-collapse w-full text-sm">
          {header}
          <tbody>
            {actionGroups.map(
              (g) =>
                g.subgroups.length > 0 && (
                  <React.Fragment key={g.labelKey}>
                    <tr>
                      <th colSpan={roles.length + 1} className="text-left p-2 bg-base-200">
                        {t(g.labelKey)}
                      </th>
                    </tr>
                    {g.subgroups.map((sub) => (
                      <React.Fragment key={sub.labelKey}>
                        <tr>
                          <th
                            colSpan={roles.length + 1}
                            className="text-left p-2 text-base-content/60 font-normal"
                          >
                            {t(sub.labelKey)}
                          </th>
                        </tr>
                        {sub.actions.map((a) => (
                          <tr key={a}>
                            <th className="border border-base-300 p-2 text-left font-normal">
                              {t(actionPermissionLabelKey(a))}
                              <small className="block text-base-content/60 break-all">{a}</small>
                            </th>
                            {roles.map((r) => (
                              <td key={r.id} className="border border-base-300 p-2 text-center">
                                {cell(
                                  r.actionPermissions.some((x) => x.actionKey === a && x.isAllowed),
                                  r.systemKey === 'SUPER_ADMIN',
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ),
            )}
          </tbody>
        </table>
      </div>
      {!actionGroups.some((g) => g.subgroups.length) && <p>{t('permissionEmpty')}</p>}
    </AdminPage>
  );
}

export default function PermissionMatrixPage() {
  const revision = useAuthStore((s) => s.sessionRevision);
  return <MatrixContent key={revision} revision={revision} />;
}
